/**
 * Logger utility for consistent logging across environments
 * Logs to console in development and sends to server in production
 * 
 * Supports configurable LOG_LEVEL via REACT_APP_LOG_LEVEL environment variable:
 * - debug: All logs (very verbose)
 * - info: Info and above (default for development)
 * - warn: Warnings and errors only
 * - error: Errors only (recommended for production)
 * - silent: No console output (server logging still works in production)
 */

// Log levels with numeric priority (higher = more severe)
export const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  SILENT: 'silent'
};

const LOG_LEVEL_PRIORITY = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4
};

/**
 * Get the minimum log level from environment variable
 * Defaults to 'debug' in development, 'error' in production
 */
const getMinLogLevel = () => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL?.toLowerCase();
  if (envLevel && LOG_LEVEL_PRIORITY[envLevel] !== undefined) {
    return envLevel;
  }
  // Default: debug in dev, error in production
  return process.env.NODE_ENV === 'production' ? 'error' : 'debug';
};

const minLogLevel = getMinLogLevel();

/**
 * Check if a log level should be output to console
 */
const shouldLog = (level) => {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[minLogLevel];
};

// Log retention configuration (in days)
const LOG_RETENTION = {
  // Keep error logs longer (30 days)
  error: 30,
  // Keep warning logs for 14 days
  warn: 14,
  // Keep info logs for 7 days
  info: 7,
  // Keep debug logs for just 3 days
  debug: 3
};

/**
 * Clean up old localStorage logs
 */
const cleanupLocalStorageLogs = () => {
  try {
    const logs = JSON.parse(localStorage.getItem('appLogs') || '[]');
    if (logs.length === 0) return;
    
    const now = new Date();
    const cleanedLogs = logs.filter(log => {
      // Parse the log timestamp
      const logDate = new Date(log.timestamp);
      // Get retention period for this log level
      const retentionDays = LOG_RETENTION[log.level] || 7;
      // Calculate if the log is still within retention period
      const ageInDays = (now - logDate) / (1000 * 60 * 60 * 24);
      return ageInDays < retentionDays;
    });
    
    // Save cleaned logs back to localStorage
    localStorage.setItem('appLogs', JSON.stringify(cleanedLogs));
  } catch (error) {
    // Just log to console as this is just cleanup
    console.error('Error cleaning localStorage logs:', error);
  }
};

// Run cleanup on initialization and periodically
const startLocalStorageCleanup = () => {
  // Clean up immediately on load
  cleanupLocalStorageLogs();
  
  // Set interval to clean up logs daily
  setInterval(cleanupLocalStorageLogs, 24 * 60 * 60 * 1000);
};

// Start the cleanup process
startLocalStorageCleanup();

/**
 * Safely stringify objects that may contain circular references
 * @param {Object} obj - The object to stringify
 * @returns {string} - The stringified object
 */
const safeStringify = (obj) => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  });
};

/**
 * Send logs to backend in production
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} details - Additional details (may contain 'component' field)
 */
const sendToServer = async (level, message, details = {}) => {
  try {
    // Ensure component is included in details for proper error categorization
    // Component can be passed in details.component or will default to 'Frontend' in backend
    const logDetails = {
      ...details,
      // Keep component in details if it exists, backend will extract it
      component: details.component || undefined
    };
    
    await fetch('/api/frontend-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify({
        level,
        message,
        details: logDetails,
        source: 'frontend',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      })
    });
  } catch (error) {
    // Fallback to localStorage if server logging fails
    try {
      const existingLogs = localStorage.getItem('appLogs');
      const logs = existingLogs ? JSON.parse(existingLogs) : [];
      
      logs.push({
        level,
        message,
        details,
        timestamp: new Date().toISOString(),
        failed: true
      });
      
      // Keep only latest 100 logs to avoid localStorage overflow
      if (logs.length > 100) logs.shift();
      localStorage.setItem('appLogs', safeStringify(logs));
    } catch (storageError) {
      // If localStorage fails, just log to console
      console.error('Failed to save log to localStorage:', storageError);
    }
  }
};

/**
 * Log with appropriate behavior based on environment and log level
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} details - Additional details
 */
const log = (level, message, details = {}) => {
  const isProduction = process.env.NODE_ENV?.toLowerCase() === 'production';
  const timestamp = new Date().toISOString();
  
  // Only output to console if level meets minimum threshold
  if (shouldLog(level)) {
    switch (level) {
      case LOG_LEVELS.DEBUG:
        console.debug(`[${timestamp}] ${message}`, details);
        break;
      case LOG_LEVELS.INFO:
        console.info(`[${timestamp}] ${message}`, details);
        break;
      case LOG_LEVELS.WARN:
        console.warn(`[${timestamp}] ${message}`, details);
        break;
      case LOG_LEVELS.ERROR:
        console.error(`[${timestamp}] ${message}`, details);
        break;
      default:
        console.log(`[${timestamp}] ${message}`, details);
    }
  }
  
  // In production: always send to server (regardless of console log level)
  if (isProduction) {
    sendToServer(level, message, details);
  }
};

/**
 * Attempt to send any cached logs from localStorage to server
 */
export const syncLocalStorageLogs = async () => {
  try {
    const logs = JSON.parse(localStorage.getItem('appLogs') || '[]');
    if (logs.length === 0) return;
    
    // Process logs in batches to avoid overloading the server
    const BATCH_SIZE = 10;
    const batches = [];
    
    // Split logs into batches
    for (let i = 0; i < logs.length; i += BATCH_SIZE) {
      batches.push(logs.slice(i, i + BATCH_SIZE));
    }
    
    // Send batches sequentially
    const successfullyProcessed = [];
    for (const batch of batches) {
      try {
        await Promise.all(batch.map(async (log) => {
          try {
            await fetch('/api/frontend-logs', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ...log,
                source: 'frontend',
                timestamp: log.timestamp,
                recovered: true
              })
            });
            successfullyProcessed.push(log);
          } catch (error) {
            // If this particular log fails, leave it in localStorage
          }
        }));
      } catch (error) {
        // If batch processing fails, continue with next batch
        console.error('Error syncing localStorage logs batch:', error);
      }
    }
    
    // Remove logs that were successfully sent
    const remainingLogs = logs.filter(log => !successfullyProcessed.includes(log));
    localStorage.setItem('appLogs', JSON.stringify(remainingLogs));
    
    return { 
      sent: successfullyProcessed.length, 
      remaining: remainingLogs.length 
    };
  } catch (error) {
    console.error('Error syncing localStorage logs:', error);
    return { sent: 0, error: error.message };
  }
};

// Try to sync localStorage logs on application start
if (process.env.NODE_ENV?.toLowerCase() === 'production') {
  // Wait for network to be stable before attempting sync
  window.addEventListener('load', () => {
    setTimeout(syncLocalStorageLogs, 5000);
  });
}

// Export individual log level methods
export const logger = {
  debug: (message, details) => log(LOG_LEVELS.DEBUG, message, details),
  info: (message, details) => log(LOG_LEVELS.INFO, message, details),
  warn: (message, details) => log(LOG_LEVELS.WARN, message, details),
  error: (message, details) => log(LOG_LEVELS.ERROR, message, details),
  // Method to manually trigger localStorage sync
  syncLocalLogs: syncLocalStorageLogs,
  // Utility methods
  getLevel: () => minLogLevel,
  isEnabled: (level) => shouldLog(level)
};

// Log the active log level on startup (only in development, only if debug is enabled)
if (process.env.NODE_ENV !== 'production' && shouldLog('info')) {
  console.info(`[Logger] Active log level: ${minLogLevel.toUpperCase()}`);
}

// Export convenience function for error logging
export const logError = (message, details) => log(LOG_LEVELS.ERROR, message, details);

export default logger; 